import * as selectors from './selectors';

import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { deleteDocuments, getOtherAttachments, uploadOtherAttachments as uploadOtherAttachmentsApi } from '../../utils/api/documentsApi';
import { deleteOtherAttachments, deleteOtherAttachmentsError, deleteOtherAttachmentsSuccess, fetchOtherAttachments, fetchOtherAttachmentsError, fetchOtherAttachmentsSuccess, uploadOtherAttachments, uploadOtherAttachmentsError, uploadOtherAttachmentsSuccess } from './actions';

import { DetailsOtherAttachmentsActionTypes } from './types';
import { toggleIsUIDisabled } from '../../shared/actions';
import { createDeleteDocumentRequest } from '../shared/model';
import { SagaIterator } from 'redux-saga';

function* handleFetchOtherAttachments(action: ReturnType<typeof fetchOtherAttachments>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const res = yield call(getOtherAttachments, action.payload.instructionId);

    if (!res.data) {
      yield put(fetchOtherAttachmentsError(JSON.stringify(res.response)));
    } else {
      yield put(fetchOtherAttachmentsSuccess(res.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchOtherAttachmentsError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchOtherAttachmentsError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleUploadOtherAttachments(action: ReturnType<typeof uploadOtherAttachments>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const otherAttachment = yield select(selectors.otherAttachment, action.payload.index);

    // Sanitize filename
    const sanitizedFilename = sanitizeFilename(otherAttachment.filename);
    const sanitizedAttachment = { ...otherAttachment, filename: sanitizedFilename };

    const claimNotificationId = yield select(selectors.claimNotificationId);
    const res = yield call(uploadOtherAttachmentsApi, action.payload.instructionId, { claimNotificationId, documents: [sanitizedAttachment] });

    if (!res.data) {
      yield put(uploadOtherAttachmentsError(JSON.stringify(res.response)));
    } else {
      yield put(uploadOtherAttachmentsSuccess(action.payload.index, res.data[0].id));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(uploadOtherAttachmentsError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(uploadOtherAttachmentsError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleDeleteOtherAttachments(action: ReturnType<typeof deleteOtherAttachments>): SagaIterator {
  yield put(toggleIsUIDisabled(true));
  const otherAttachment = yield select(selectors.otherAttachment, action.payload.index);
  try {
    if (otherAttachment.isOnline) {
      const deleteRequest = createDeleteDocumentRequest();
      deleteRequest.amazonId = otherAttachment.amazonId;
      deleteRequest.id = otherAttachment.id;

      const res = yield call(deleteDocuments, [deleteRequest]);

      if (!res.data) {
        yield put(deleteOtherAttachmentsError(JSON.stringify(res.response)));
      } else {
        yield put(deleteOtherAttachmentsSuccess(action.payload.index));
      }
    } else {
      yield put(deleteOtherAttachmentsSuccess(action.payload.index));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(deleteOtherAttachmentsError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(deleteOtherAttachmentsError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function sanitizeFilename(filename: string): string {
  if (!filename || filename.trim() === '') {
    return ''; // or any default fallback name you prefer
  }
  // Normalize to NFD form and remove diacritics
  return filename
    .normalize('NFD') // Split accented characters into base + diacritic
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritic marks
    .replace(/[^\x00-\x7F]/g, '') // Remove any remaining non-ASCII characters
    .replace(/[^a-zA-Z0-9._-]/g, '_'); // Replace unsafe characters with underscore
}

function* watchOtherAttachmentsDetails() {
  yield takeEvery(DetailsOtherAttachmentsActionTypes.FETCH_OTHER_ATTACHMENTS, handleFetchOtherAttachments);
  yield takeEvery(DetailsOtherAttachmentsActionTypes.UPLOAD_OTHER_ATTACHMENTS, handleUploadOtherAttachments);
  yield takeEvery(DetailsOtherAttachmentsActionTypes.DELETE_OTHER_ATTACHMENTS, handleDeleteOtherAttachments);
}

function* detailsOtherAttachmentsSaga() {
  yield all([fork(watchOtherAttachmentsDetails)]);
}

export default detailsOtherAttachmentsSaga;
